import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [isRecovering, setIsRecovering] = useState(false);

  const { login, isAuthenticated, error: authError, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (user.requiereCambioPassword) {
      navigate('/cambiar-password-temporal', { replace: true });
      return;
    }
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  }, [isAuthenticated, user, navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!email.trim() || !password.trim()) {
      setLocalError('Por favor, completa todos los campos');
      return;
    }

    setIsSubmitting(true);

    try {
      const loggedIn = await login(email, password);

      if (loggedIn.requiereCambioPassword) {
        navigate('/cambiar-password-temporal', { replace: true });
        setIsSubmitting(false);
        return;
      }

      const from = location.state?.from?.pathname;
      let redirectPath = '/investigadores';

      if (from && from !== '/login' && from !== '/cambiar-password-temporal') {
        redirectPath = from;
      } else if (loggedIn.rol === 'SuperAdmin' || loggedIn.rol === 'ADMIN_CEDIA') {
        redirectPath = '/dashboards';
      } else if (loggedIn.rol === 'Administrador' || loggedIn.rol === 'ADMIN_IES') {
        redirectPath = '/investigadores';
      } else {
        redirectPath = '/publicaciones';
      }

      navigate(redirectPath, { replace: true });
    } catch (error) {
      console.error('Error en login:', error);
      setLocalError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openForgotModal = () => {
    setForgotEmail(email || '');
    setForgotError('');
    setForgotSuccess('');
    setShowForgotModal(true);
  };

  const closeForgotModal = () => {
    if (isRecovering) return;
    setShowForgotModal(false);
    setForgotError('');
    setForgotSuccess('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');

    if (!forgotEmail.trim()) {
      setForgotError('Por favor, ingresa tu correo registrado');
      return;
    }

    setIsRecovering(true);
    try {
      const response = await authService.forgotPassword(forgotEmail.trim());
      setForgotSuccess(
        response.message || 'Se enviaron instrucciones de recuperacion al correo registrado.'
      );
    } catch (error) {
      setForgotError(
        error.response?.data?.detail || error.message || 'No se pudo procesar la recuperacion'
      );
    } finally {
      setIsRecovering(false);
    }
  };

  const errorMessage = localError || authError;

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#00125b] text-slate-900 dark:text-slate-100"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(93,139,217,0.22),_transparent_38%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_28%)]" />
      <div className="absolute inset-0 bg-[#00125b]/20" />

      <div className="relative z-10 flex min-h-screen flex-col overflow-x-hidden px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-5xl flex-1 items-center justify-center">
          <div className="grid w-full items-stretch gap-5 lg:grid-cols-[minmax(0,0.95fr)_minmax(320px,460px)] lg:gap-8">
            <section className="hidden lg:flex flex-col justify-center rounded-[30px] border border-white/10 bg-white/8 p-8 text-white shadow-[0_30px_80px_rgba(0,0,0,0.28)] backdrop-blur-sm xl:p-9">
              <span className="mb-5 inline-flex w-fit items-center rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-blue-100">
                Plataforma SIGC
              </span>
              <h1 className="max-w-xl text-3xl font-bold leading-tight xl:text-[2.8rem]">
                Sistema Inteligente de Gestion del Conocimiento
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-7 text-blue-100/90 xl:text-base">
                Centraliza investigadores, publicaciones y tableros cientificos en un solo espacio,
                con un acceso mucho mas claro desde cualquier pantalla.
              </p>

              <div className="mt-7 grid gap-3 sm:grid-cols-3">
                <div className="rounded-[22px] border border-white/12 bg-white/10 p-4">
                  <span className="material-symbols-outlined text-2xl text-blue-100">insights</span>
                  <p className="mt-3 text-sm font-semibold">Dashboards estrategicos</p>
                </div>
                <div className="rounded-[22px] border border-white/12 bg-white/10 p-4">
                  <span className="material-symbols-outlined text-2xl text-blue-100">groups</span>
                  <p className="mt-3 text-sm font-semibold">Gestion de investigadores</p>
                </div>
                <div className="rounded-[22px] border border-white/12 bg-white/10 p-4">
                  <span className="material-symbols-outlined text-2xl text-blue-100">library_books</span>
                  <p className="mt-3 text-sm font-semibold">Seguimiento de publicaciones</p>
                </div>
              </div>
            </section>

            <section className="w-full overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800 sm:rounded-[32px]">
              <div className="border-b border-slate-100 bg-white px-5 py-5 dark:border-slate-700 dark:bg-slate-800 sm:px-7 sm:py-6">
                <div className="mx-auto flex max-w-md flex-col items-center text-center">
                  <div className="mb-3 flex h-20 items-center justify-center sm:h-24 md:h-28">
                    <img
                      alt="CEDIA Logo"
                      className="max-h-full w-auto object-contain"
                      src="/images/cedia.webp"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const placeholder = document.createElement('div');
                        placeholder.className = 'flex h-24 w-40 items-center justify-center rounded-2xl bg-[#2D5BA6] text-2xl font-bold text-white sm:h-28 sm:w-48';
                        placeholder.textContent = 'CEDIA';
                        e.target.parentElement.appendChild(placeholder);
                      }}
                    />
                  </div>

                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#2D5BA6] dark:text-[#8FB7FF]">
                    Acceso seguro
                  </p>
                  <h2 className="text-balance text-xl font-bold text-slate-900 dark:text-white sm:text-2xl">
                    Inicia sesion en SIGC
                  </h2>
                  <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Ingresa con tu cuenta institucional para continuar con la gestion del sistema.
                  </p>
                </div>
              </div>

              <div className="px-5 py-5 sm:px-7 sm:py-6">
                <div className="mx-auto max-w-md">
                  {errorMessage && (
                    <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                      <span className="material-symbols-outlined mt-0.5 text-xl text-red-600 dark:text-red-400">
                        error
                      </span>
                      <p className="flex-1 text-sm leading-6 text-red-800 dark:text-red-200">
                        {errorMessage}
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
                      >
                        Correo electronico
                      </label>
                      <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-lg text-slate-400">
                          mail
                        </span>
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 transition-all placeholder:text-slate-400 focus:border-[#2D5BA6] focus:ring-2 focus:ring-[#2D5BA6]/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                          placeholder="usuario@cedia.edu.ec"
                          disabled={isSubmitting}
                          autoComplete="email"
                          required
                        />
                      </div>
                      
                    </div>

                    <div>
                      <label
                        htmlFor="password"
                        className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
                      >
                        Contrasena
                      </label>
                      <div className="relative">
                        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-lg text-slate-400">
                          lock
                        </span>
                        <input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-12 text-sm text-slate-900 transition-all placeholder:text-slate-400 focus:border-[#2D5BA6] focus:ring-2 focus:ring-[#2D5BA6]/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                          placeholder="••••••••••"
                          disabled={isSubmitting}
                          autoComplete="current-password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-300"
                          tabIndex="-1"
                        >
                          <span className="material-symbols-outlined text-lg">
                            {showPassword ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={openForgotModal}
                          disabled={isSubmitting}
                          className="text-sm font-medium text-[#2D5BA6] transition-colors hover:text-[#244a8a] disabled:opacity-40"
                        >
                          Olvide mi contrasena
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3.5 text-sm font-semibold text-white transition-all duration-300 ${
                        isSubmitting
                          ? 'cursor-not-allowed bg-slate-400'
                          : 'bg-[#2D5BA6] shadow-md hover:bg-[#244a8a] hover:shadow-lg'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          <span>Iniciando sesion...</span>
                        </>
                      ) : (
                        <>
                          <span className="material-symbols-outlined text-lg">login</span>
                          <span>Iniciar Sesion</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>

        {showForgotModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4">
            <div className="w-full max-w-md rounded-[28px] border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-800 sm:p-7">
              <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#2D5BA6] dark:text-[#8FB7FF]">
                  Recuperacion de acceso
                </p>
                <h3 className="mt-2 text-xl font-bold text-slate-900 dark:text-white">
                  Recuperar contrasena
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Ingresa el correo registrado de tu cuenta. Si existe, te enviaremos una contrasena temporal y al ingresar deberas cambiarla inmediatamente.
                </p>
              </div>

              {forgotError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                  {forgotError}
                </div>
              )}

              {forgotSuccess && (
                <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
                  {forgotSuccess}
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label
                    htmlFor="forgotEmail"
                    className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    Correo electronico registrado
                  </label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-lg text-slate-400">
                      mail
                    </span>
                    <input
                      id="forgotEmail"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 transition-all placeholder:text-slate-400 focus:border-[#2D5BA6] focus:ring-2 focus:ring-[#2D5BA6]/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                      placeholder="usuario@institucion.edu.ec"
                      disabled={isRecovering}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={closeForgotModal}
                    disabled={isRecovering}
                    className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/40"
                  >
                    Cerrar
                  </button>
                  <button
                    type="submit"
                    disabled={isRecovering}
                    className={`rounded-2xl px-4 py-3 text-sm font-semibold text-white transition-all ${
                      isRecovering
                        ? 'cursor-not-allowed bg-slate-400'
                        : 'bg-[#2D5BA6] hover:bg-[#244a8a]'
                    }`}
                  >
                    {isRecovering ? 'Enviando...' : 'Enviar recuperacion'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <footer className="mx-auto mt-6 w-full max-w-5xl px-2 text-center sm:mt-8">
          <p className="text-xs leading-6 text-slate-300/80">
            © 2026 CEDIA - Sistema Inteligente de Gestion del Conocimiento. Todos los derechos reservados.
          </p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-300/60 sm:gap-4">
            <span>Infraestructura Academica</span>
            <span className="hidden sm:inline opacity-40">|</span>
            <span>I+D+i</span>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Login;
